import React, { ReactNode, Suspense } from 'react';
import ReactDOM from 'react-dom';
import loader from '@monaco-editor/loader';
import { FlagsProvider } from 'react-feature-flags';
import { ConfigProvider, Empty } from 'antd';
import { RecoilRoot } from 'recoil';
import ruRu from 'antd/lib/locale/ru_RU';
import 'moment/locale/ru';
import moment from 'moment';

import './index.less';
import './i18n';

import { isErrorRoute, processAuth } from './utils/oidcUtil';
import { includesIgnoreCase } from './utils/stringUtil';
import { checkActivation } from './utils/activationUtil';
import App from './App';
import ErrorApp from './ErrorApp';
import ActivationRequiredPage from './pages/activation/ActivationRequiredPage';
import * as serviceWorker from './serviceWorker';
import { getFeatureFlags } from './utils/featureFlagsUtil';
import IbAlerts from './components/IbAlerts';

loader.config({ paths: { vs: '/monaco-editor/vs' } });

moment.locale(window.navigator.language);

const preloader = (
  <div className="preloader">
    <div className="preloader__content"></div>
  </div>
);

const renderPage = (component: ReactNode) => {
  ReactDOM.render(
    <Suspense fallback={preloader}>
      <ConfigProvider>{component}</ConfigProvider>
    </Suspense>,
    document.getElementById('root')
  );
};

(async () => {
  if (isErrorRoute()) {
    renderPage(<ErrorApp />);
    return;
  }

  const activationResult = await checkActivation();

  if (!activationResult.isActivated) {
    renderPage(<ActivationRequiredPage status={activationResult.status} />);
    return;
  }

  if (!(await processAuth())) {
    return;
  }

  const customizeRenderEmpty = () => <Empty description="Нет данных" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const locale = includesIgnoreCase(navigator.language, 'ru') ? ruRu : undefined;

  const featureFlags = await getFeatureFlags();

  ReactDOM.render(
    <Suspense fallback={preloader}>
      <FlagsProvider value={featureFlags}>
        <RecoilRoot>
          <ConfigProvider locale={locale} renderEmpty={customizeRenderEmpty}>
            <IbAlerts />
            <App />
          </ConfigProvider>
        </RecoilRoot>
      </FlagsProvider>
    </Suspense>,
    document.getElementById('root')
  );

  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: https://bit.ly/CRA-PWA
  serviceWorker.unregister();
})();
